/**
 * ScaleOrderModal — creates a ladder of N limit orders spread evenly
 * across a user-defined price range in a single click.
 */
import React, { useState, useCallback, useMemo } from "react";
import { t, Trans } from "@lingui/macro";

import Modal from "components/Modal/Modal";
import { getContract } from "config/contracts";
import { getConstant } from "config/chains";
import { helperToast } from "lib/helperToast";
import { createIncreaseOrder } from "domain/legacy";

import "./ScaleOrderModal.css";

type Props = {
  isVisible: boolean;
  onClose: () => void;
  chainId: number;
  signer: any;
  account: string | undefined;
  fromTokenAddress: string;
  indexTokenAddress: string;
  collateralTokenAddress: string;
  nativeTokenAddress: string;
  isLong: boolean;
  setPendingTxns?: (txns: any) => void;
  orderBookApproved?: boolean;
  approveOrderBook?: () => any;
  payTokenSymbol?: string;
};

function priceToP30(usd: number): bigint {
  if (!usd || usd <= 0) return 0n;
  return BigInt(Math.round(usd * 1e8)) * 10n ** 22n;
}

export function ScaleOrderModal({
  isVisible,
  onClose,
  chainId,
  signer,
  account,
  fromTokenAddress,
  indexTokenAddress,
  collateralTokenAddress,
  nativeTokenAddress,
  isLong,
  setPendingTxns,
  orderBookApproved,
  approveOrderBook,
  payTokenSymbol,
}: Props) {
  const [totalCollateral, setTotalCollateral] = useState("");
  const [startPrice, setStartPrice] = useState("");
  const [endPrice, setEndPrice] = useState("");
  const [numOrders, setNumOrders] = useState("5");
  const [submitting, setSubmitting] = useState(false);

  const preview = useMemo(() => {
    const n = parseInt(numOrders, 10);
    const start = parseFloat(startPrice);
    const end = parseFloat(endPrice);
    const collateral = parseFloat(totalCollateral);
    if (!n || n < 2 || !start || !end || !collateral) return null;

    const perOrder = collateral / n;
    const rows = Array.from({ length: n }, (_, i) => {
      const price = start + (i * (end - start)) / (n - 1);
      return { price: price.toFixed(2), collateral: perOrder.toFixed(4) };
    });
    return rows;
  }, [numOrders, startPrice, endPrice, totalCollateral]);

  const handleSubmit = useCallback(async () => {
    const n = parseInt(numOrders, 10);
    const start = parseFloat(startPrice);
    const end = parseFloat(endPrice);
    const totalCol = parseFloat(totalCollateral);

    if (!n || n < 2 || n > 10) return helperToast.error(t`Orders must be 2–10`);
    if (!start || !end || start <= 0 || end <= 0) return helperToast.error(t`Enter valid prices`);
    if (!totalCol || totalCol <= 0) return helperToast.error(t`Enter valid collateral`);
    if (!account || !signer) return;

    // Scale orders execute through the OrderBook — require the one-time plugin
    // ("Enable Orders") approval first, or the created orders can never execute.
    if (orderBookApproved === false) {
      if (approveOrderBook) approveOrderBook();
      helperToast.error(t`Enable Orders first, then submit your scale order.`);
      return;
    }

    const perOrderCollateral = totalCol / n;
    // Convert collateral to BigInt (assume 18 decimals for most tokens)
    const amountIn = BigInt(Math.round(perOrderCollateral * 1e18));
    const path = fromTokenAddress === collateralTokenAddress
      ? [fromTokenAddress]
      : [fromTokenAddress, collateralTokenAddress];

    const executionFee = getConstant(chainId, "INCREASE_ORDER_EXECUTION_GAS_FEE") as bigint;

    setSubmitting(true);
    try {
      const txns = Array.from({ length: n }, (_, i) => {
        const price = start + (i * (end - start)) / (n - 1);
        const triggerPrice = priceToP30(price);
        const sizeDelta = amountIn; // 1:1 collateral→size (leverage handled at router level)

        return createIncreaseOrder(
          chainId,
          signer,
          nativeTokenAddress,
          path,
          amountIn,
          indexTokenAddress,
          0,
          sizeDelta,
          collateralTokenAddress,
          isLong,
          triggerPrice,
          {
            setPendingTxns,
            sentMsg: t`Scale order ${i + 1}/${n} submitted`,
            failMsg: t`Scale order ${i + 1} failed`,
            hideSuccessMsg: true,
          }
        ).catch((err: Error) => {
          console.error(`Scale order ${i + 1} failed:`, err);
        });
      });

      await Promise.all(txns);
      helperToast.success(t`${n} scale orders submitted`);
      onClose();
    } catch {
      helperToast.error(t`Scale orders failed`);
    } finally {
      setSubmitting(false);
    }
  }, [
    numOrders,
    startPrice,
    endPrice,
    totalCollateral,
    account,
    signer,
    chainId,
    fromTokenAddress,
    indexTokenAddress,
    collateralTokenAddress,
    nativeTokenAddress,
    isLong,
    setPendingTxns,
    onClose,
  ]);

  return (
    <Modal isVisible={isVisible} setIsVisible={onClose} label={t`Scale Orders`}>
      <div className="SM-root">
        <p className="SM-desc">
          <Trans>
            Creates {numOrders || "N"} limit orders evenly spaced across your price range,
            each using an equal share of your total collateral.
          </Trans>
        </p>

        <div className="SM-field">
          <label className="SM-label"><Trans>Amount to ladder</Trans></label>
          <div className="SM-input-wrap">
            <input
              className="SM-input"
              type="number"
              placeholder="0.0"
              value={totalCollateral}
              onChange={(e) => setTotalCollateral(e.target.value)}
              disabled={submitting}
            />
            <span className="SM-input-token">{payTokenSymbol || t`tokens`}</span>
          </div>
        </div>

        <div className="SM-row">
          <div className="SM-field SM-field-half">
            <label className="SM-label"><Trans>Start Price $</Trans></label>
            <input
              className="SM-input"
              type="number"
              placeholder="e.g. 95000"
              value={startPrice}
              onChange={(e) => setStartPrice(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="SM-field SM-field-half">
            <label className="SM-label"><Trans>End Price $</Trans></label>
            <input
              className="SM-input"
              type="number"
              placeholder="e.g. 90000"
              value={endPrice}
              onChange={(e) => setEndPrice(e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>

        <div className="SM-field SM-field-narrow">
          <label className="SM-label"><Trans>Number of orders (2–10)</Trans></label>
          <input
            className="SM-input"
            type="number"
            min={2}
            max={10}
            value={numOrders}
            onChange={(e) => setNumOrders(e.target.value)}
            disabled={submitting}
          />
        </div>

        {preview && (
          <div className="SM-preview">
            <div className="SM-preview-header">
              <span><Trans>Price</Trans></span>
              <span><Trans>Collateral</Trans></span>
            </div>
            {preview.map((row, i) => (
              <div key={i} className="SM-preview-row">
                <span className="SM-price">${row.price}</span>
                <span className="SM-col">{row.collateral}</span>
              </div>
            ))}
          </div>
        )}

        <div className="SM-actions">
          <button
            className="SM-btn-submit"
            onClick={handleSubmit}
            disabled={submitting || !preview}
          >
            {submitting ? t`Submitting…` : t`Place ${numOrders || "N"} Orders`}
          </button>
          <button
            className="SM-btn-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            <Trans>Cancel</Trans>
          </button>
        </div>
      </div>
    </Modal>
  );
}
